import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, signal } from '@angular/core';
import { groupBy } from '../../utils/group-by';
import { guid } from '../../utils/guid';
import { formatAmount, formatNumber, normalizeAmount } from '../../utils/format-amount';
import { sortBy } from '../../utils/sort-by';
import { BomStructure } from '../../models/bom-structure';

export interface TreeItem {
  data: {
    bomItemId?: number;
    name: string;
    amount: number;
    unit: string;
    // the item's own mass (0068): per piece for a szt row, per segment for a length row — absent on order snapshots
    massKg?: number;
    // Opis (0096), shown in the row's tooltip — absent on order snapshots
    description?: string;
    // a node without it (a placeholder row) is not purchased
    structure?: BomStructure;
  };
  children: TreeItem[];
}

export interface ListItem {
  id: string;
  bomItemId?: number;
  name: string;
  description?: string;
  amount: number;
  // sums stay within one bomItemId, so within one unit — no cross-unit arithmetic can occur
  unit: string;
  // summed over the item's occurrences by 0077's rule; undefined when its node carries no mass
  massKg?: number;
  type: 'product' | 'module' | 'material';
}

// one WG TYPU section: the type's aggregated items, stacked under the type's header row (0089)
export interface TableSection {
  type: ListItem['type'];
  items: ListItem[];
}

// 'leading' is the optional first tab whose content the parent projects (the import preview's DRZEWO)
export type SelectedTab = 'leading' | 'list' | 'table';

const TYPE_ORDER: ListItem['type'][] = ['product', 'module', 'material'];

export const TYPE_LABELS: Record<ListItem['type'], string> = {
  product: 'Produkt',
  module: 'Półprodukt',
  material: 'Materiał'
};

@Component({
  selector: 'tree-summary',
  templateUrl: './tree-summary.component.html',
  styleUrl: 'tree-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: []
})
export class TreeSummaryComponent {
  formatAmount = formatAmount;


  readonly typeLabels = TYPE_LABELS;

  readonly items = input<TreeItem[]>();

  // what the roots of 'items' are in their full BOM — the component cannot tell a scoped subtree root from a real product
  readonly rootType = input<ListItem['type']>('product');

  // when set, the tab row starts with this word, selected at start, and shows the projected content while it is selected
  readonly leadingTab = input<string>();

  readonly aggregatedItems = computed<ListItem[] | undefined>(() => {
    const items = this.items();

    if (!items) {
      return undefined;
    }

    return this.foldForest(items);
  });

  // the mass column exists only when some item has one, so a summary of order snapshots draws as it always did
  readonly hasMass = computed(() => this.aggregatedItems()?.some(item => item.massKg != null) ?? false);

  readonly tableSections = computed<TableSection[] | undefined>(() => {
    const listItems = this.aggregatedItems();

    if (!listItems) {
      return undefined;
    }

    const sections = Object
      .entries(groupBy(listItems, item => item.type))
      .map(([key, items]) => ({ type: key as ListItem['type'], items }));

    return sortBy(sections, section => TYPE_ORDER.indexOf(section.type));
  });

  readonly selectedTab = linkedSignal<SelectedTab>(() => this.leadingTab() ? 'leading' : 'list');

  // Kopiuj reads Skopiowano for a moment after the clipboard took the rows (0119)
  readonly copied = signal(false);

  private copiedTimeout?: ReturnType<typeof setTimeout>;

  changeSelectedTab(tab: SelectedTab): void {
    this.selectedTab.set(tab);
  }

  // the tab that is showing as tab-separated rows, so a paste into Excel or an e-mail lands as a table (0119); a refused
  // clipboard leaves the label as it was
  copy(): void {
    const text = this.selectedTab() === 'table' ? this.tableText() : this.listText();

    navigator.clipboard.writeText(text).then(() => {
      this.copied.set(true);
      clearTimeout(this.copiedTimeout);
      this.copiedTimeout = setTimeout(() => this.copied.set(false), 2000);
    });
  }

  // LISTA: Nazwa, Ilość, Jedn. and Masa [kg] when the card shows a mass column
  listText(): string {
    const header = ['Nazwa', 'Ilość', 'Jedn.', ...(this.hasMass() ? ['Masa [kg]'] : [])];
    const rows = (this.aggregatedItems() ?? []).map(item => this.copyRow(item));

    return toTsv([header, ...rows]);
  }

  // WG TYPU: the type as its own first column, rows in section order, so a paste can be filtered to Materiał
  tableText(): string {
    const header = ['Typ', 'Nazwa', 'Ilość', 'Jedn.', ...(this.hasMass() ? ['Masa [kg]'] : [])];
    const rows = (this.tableSections() ?? [])
      .flatMap(section => section.items.map(item => [this.typeLabels[section.type], ...this.copyRow(item)]));

    return toTsv([header, ...rows]);
  }

  // the figures the card prints, with the unit in its own column so Polish Excel reads the amount as a number; an empty
  // mass cell where the card shows a dash
  private copyRow(item: ListItem): string[] {
    const row = [item.name, formatNumber(item.amount), item.unit];

    return this.hasMass() ? [...row, item.massKg != null ? formatNumber(item.massKg) : ''] : row;
  }

  // the full name (0078) plus the description when it says something the name does not (0096)
  tooltip(item: ListItem): string {
    const description = item.description?.trim();

    return description && description !== item.name.trim() ? `${item.name}\n${description}` : item.name;
  }

  private foldForest(items: TreeItem[]): ListItem[] {
    const result: { [bomItemId: string]: ListItem } = {};

    for (const root of items) {
      for (const aggregatedItem of this.aggregateRecursive(root)) {
        const key = aggregatedItem.bomItemId ?? aggregatedItem.id;
        const savedItem = result[key];
        result[key] = !savedItem ? aggregatedItem : {
          ...savedItem,
          amount: normalizeAmount(savedItem.amount + aggregatedItem.amount),
          massKg: sumMass(savedItem.massKg, aggregatedItem.massKg)
        };
      }
    }

    const foldedItems = Object.values(result);

    return sortBy(foldedItems, x => x.name);
  }

  private *aggregateRecursive(node: TreeItem, parentAmount = 1, depthLevel = 0): IterableIterator<ListItem> {
    // normalized (0111) like the API's snapshot totals: 3 × 18.6 m is 55.8 m
    const currentAmount = normalizeAmount(node.data.amount * parentAmount);

    // 0118's rule, so the card and Raporty never disagree (0120): a purchased part is the material the plant buys, and
    // nothing under it counts — under nested purchased nodes the topmost one wins by the same stop
    const purchased = node.data.structure === 'Purchased';

    const aggregatedItem: ListItem = {
      ...node.data,
      id: guid(),
      amount: currentAmount,
      massKg: occurrenceMass(node.data, currentAmount, parentAmount),
      type: purchased ? 'material' : depthLevel === 0 ? this.rootType() : node.children.length === 0 ? 'material' : 'module'
    };

    yield aggregatedItem;

    if (purchased) {
      return;
    }

    for (const child of node.children) {
      yield* this.aggregateRecursive(child, currentAmount, depthLevel + 1);
    }
  }
}

// 0077's rule: a szt row's mass is per piece, so it counts times the pieces; a row in any other unit carries the mass
// of its segment, counted once per occurrence of its parent
function occurrenceMass(data: TreeItem['data'], currentAmount: number, parentAmount: number): number | undefined {
  if (data.massKg == null) {
    return undefined;
  }

  return roundMass(data.unit === 'szt' ? data.massKg * currentAmount : data.massKg * parentAmount);
}

// a tab or a line break inside a name would split its row, so it reads as a space
function toTsv(rows: string[][]): string {
  return rows.map(row => row.map(cell => cell.replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\n');
}

function sumMass(a: number | undefined, b: number | undefined): number | undefined {
  return a == null || b == null ? undefined : roundMass(a + b);
}

// three places, as the API rounds the product's rollup — 0.3 × 3 must print 0.9
function roundMass(massKg: number): number {
  return Math.round(massKg * 1000) / 1000;
}
